/* assets/section-tabbed-reviews.js */
(() => {
  'use strict';

  const SECTION_SEL = '.tabbed-reviews';

  function positionHighlight(root) {
    const tabsEl = root.querySelector('.tabbed-reviews__tabs');
    const highlight = root.querySelector('.tabbed-reviews__tab-highlight');
    if (!tabsEl || !highlight) return;

    const active =
      tabsEl.querySelector('.tabbed-reviews__tab.is-active') ||
      tabsEl.querySelector('.tabbed-reviews__tab');
    if (!active) return;

    const left = active.offsetLeft - tabsEl.scrollLeft;
    highlight.style.width = `${active.offsetWidth}px`;
    highlight.style.transform = `translateX(${left}px)`;
  }

  function destroySwipers(root) {
    const instances = root._tabbedReviewsSwipers || [];
    instances.forEach((swiper) => {
      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
      }
    });
    root._tabbedReviewsSwipers = [];
  }

  function initSwiper(panel) {
    const container = panel.querySelector('.tabbed-reviews__swiper');
    if (!container || typeof Swiper === 'undefined') return null;
    if (container.swiper) return container.swiper;

    const scrollbarEl = panel.querySelector('.tabbed-reviews__scrollbar');
    const prevEl = panel.querySelector('.tabbed-reviews__nav--prev');
    const nextEl = panel.querySelector('.tabbed-reviews__nav--next');
    const slideCount = container.querySelectorAll('.swiper-slide').length;

    return new Swiper(container, {
      slidesPerView: 1,
      spaceBetween: 16,
      speed: 550,
      grabCursor: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      navigation:
        prevEl || nextEl
          ? {
              prevEl,
              nextEl,
            }
          : undefined,
      breakpoints: {
        750: {
          slidesPerView: Math.min(2, slideCount),
          spaceBetween: 20,
        },
        990: {
          slidesPerView: Math.min(3, slideCount),
          spaceBetween: 24,
        },
      },
      scrollbar: scrollbarEl
        ? {
            el: scrollbarEl,
            draggable: true,
            hide: false,
          }
        : undefined,
    });
  }

  function ensurePanelSwiper(root, panel) {
    if (!panel) return null;
    const container = panel.querySelector('.tabbed-reviews__swiper');
    if (!container) return null;
    if (container.swiper) {
      container.swiper.update();
      return container.swiper;
    }
    const swiper = initSwiper(panel);
    if (swiper) {
      if (!root._tabbedReviewsSwipers) root._tabbedReviewsSwipers = [];
      root._tabbedReviewsSwipers.push(swiper);
    }
    return swiper;
  }

  function switchTab(root, index) {
    const tabs = root.querySelectorAll('.tabbed-reviews__tab');
    const panels = root.querySelectorAll('.tabbed-reviews__panel');

    tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle('is-active', isActive);
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach((panel, i) => {
      const isActive = i === index;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    positionHighlight(root);

    const activePanel = panels[index];
    requestAnimationFrame(() => {
      const swiper = ensurePanelSwiper(root, activePanel);
      if (swiper) swiper.slideTo(0, 0);
    });
  }

  function init(root) {
    if (!root || root.dataset.tabbedReviewsInit === 'true') return;

    const tabs = root.querySelectorAll('.tabbed-reviews__tab');
    const panels = root.querySelectorAll('.tabbed-reviews__panel');

    if (panels.length === 0) return;

    root._tabbedReviewsSwipers = [];

    panels.forEach((panel, index) => {
      const isActive = index === 0;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    ensurePanelSwiper(root, panels[0]);

    requestAnimationFrame(() => positionHighlight(root));

    const onResize = () => positionHighlight(root);
    root._tabbedReviewsOnResize = onResize;
    window.addEventListener('resize', onResize, { passive: true });

    const tabsEl = root.querySelector('.tabbed-reviews__tabs');
    if (tabsEl) {
      const onScroll = () => positionHighlight(root);
      root._tabbedReviewsOnScroll = onScroll;
      tabsEl.addEventListener('scroll', onScroll, { passive: true });
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => switchTab(root, index));

      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (index + direction + tabs.length) % tabs.length;
        tabs[nextIndex].focus();
        switchTab(root, nextIndex);
      });
    });

    root.dataset.tabbedReviewsInit = 'true';
  }

  function teardown(root) {
    if (!root) return;
    destroySwipers(root);
    if (root._tabbedReviewsOnResize) {
      window.removeEventListener('resize', root._tabbedReviewsOnResize);
      delete root._tabbedReviewsOnResize;
    }
    if (root._tabbedReviewsOnScroll) {
      const tabsEl = root.querySelector('.tabbed-reviews__tabs');
      if (tabsEl) tabsEl.removeEventListener('scroll', root._tabbedReviewsOnScroll);
      delete root._tabbedReviewsOnScroll;
    }
    delete root.dataset.tabbedReviewsInit;
  }

  function initAll() {
    document.querySelectorAll(SECTION_SEL).forEach(init);
  }

  function whenSwiperReady(cb, attempts) {
    const max = attempts || 40;
    if (typeof Swiper !== 'undefined') {
      cb();
      return;
    }
    if (max <= 0) {
      console.warn('tabbed-reviews: Swiper library not loaded');
      cb();
      return;
    }
    setTimeout(() => whenSwiperReady(cb, max - 1), 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => whenSwiperReady(initAll));
  } else {
    whenSwiperReady(initAll);
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector(SECTION_SEL) || (event.target.matches?.(SECTION_SEL) ? event.target : null);
    if (!root) return;
    teardown(root);
    whenSwiperReady(() => init(root));
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector(SECTION_SEL) || (event.target.matches?.(SECTION_SEL) ? event.target : null);
    if (root) teardown(root);
  });
})();
